import { create } from 'zustand'

import {
  getAllContacts,
  createContact as pbCreateContact,
  updateContact as pbUpdateContact,
  deleteContact as pbDeleteContact,
  getContact as pbGetContact,
  addContactNote as pbAddContactNote,
  draftContactEmail as pbDraftContactEmail,
  sendContactEmail as pbSendContactEmail,
  updateContactStage as pbUpdateContactStage,
} from '../api/contactsService'
import type { PBContact, PBContactNote, PBContactPipelineLog } from '../api/contactsService'

export type ContactStatus = 'lead' | 'prospect' | 'client' | 'inactive'
export type ContactStage = 'new' | 'contacted' | 'proposal' | 'won' | 'lost'
export type ContactSource = 'website' | 'landing_booking' | 'landing_contact' | 'referral' | 'social' | 'manual' | 'other'

export interface CRMContact {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  position: string
  status: ContactStatus
  stage: ContactStage
  source: ContactSource
  coursesInterested: string[]
  lastContact: string
  notes: string
  assignedTo: string | null
  assignedToName: string | null
  notesHistory: CRMContactNote[]
  pipelineLogs: CRMContactPipelineLog[]
  createdAt: string
  updatedAt: string
}

export interface CRMContactNote {
  id: string
  contact: string
  noteType: string
  content: string
  createdBy: string | null
  createdByName: string | null
  createdAt: string
}

export interface CRMContactPipelineLog {
  id: string
  stepType: string
  description: string
  metadata: Record<string, unknown>
  createdAt: string
}

export interface CRMState {
  contacts: CRMContact[]
  currentContact: CRMContact | null
  isLoading: boolean
  error: string | null
  loadContacts: () => Promise<void>
  loadContact: (id: string) => Promise<void>
  addContact: (data: Omit<CRMContact, 'id' | 'createdAt' | 'updatedAt' | 'notesHistory' | 'assignedToName' | 'pipelineLogs'>) => Promise<CRMContact>
  updateContact: (id: string, data: Partial<CRMContact>) => Promise<void>
  deleteContact: (id: string) => Promise<void>
  addNote: (contactId: string, data: { noteType: string; content: string }) => Promise<void>
  draftEmail: (contactId: string, data?: { purpose?: string; tone?: string }) => Promise<{ subject: string; body: string }>
  sendEmail: (contactId: string, data: { subject: string; message: string }) => Promise<void>
  updateStage: (id: string, stage: ContactStage) => Promise<void>
}

function pbToContact(r: PBContact): CRMContact {
  return {
    id: r.id,
    firstName: r.firstName,
    lastName: r.lastName,
    email: r.email ?? '',
    phone: r.phone ?? '',
    company: r.company ?? '',
    position: r.position ?? '',
    status: r.status,
    stage: r.stage,
    source: (r.source as ContactSource) || 'manual',
    coursesInterested: Array.isArray(r.coursesInterested) ? r.coursesInterested : [],
    lastContact: r.lastContact ?? '',
    notes: r.notes ?? '',
    assignedTo: r.assignedTo ?? null,
    assignedToName: r.assignedToName ?? null,
    notesHistory: Array.isArray(r.notesHistory) ? r.notesHistory.map(pbToNote) : [],
    pipelineLogs: Array.isArray(r.pipelineLogs) ? r.pipelineLogs.map(pbToPipelineLog) : [],
    createdAt: r.createdAt ?? '',
    updatedAt: r.updatedAt ?? '',
  }
}

function pbToPipelineLog(r: PBContactPipelineLog): CRMContactPipelineLog {
  return {
    id: r.id,
    stepType: r.stepType,
    description: r.description,
    metadata: r.metadata ?? {},
    createdAt: r.createdAt,
  }
}

function pbToNote(r: PBContactNote): CRMContactNote {
  return {
    id: r.id,
    contact: r.contact,
    noteType: r.noteType,
    content: r.content,
    createdBy: r.createdBy ?? null,
    createdByName: r.createdByName ?? null,
    createdAt: r.createdAt,
  }
}

export const useCRMStore = create<CRMState>((set) => ({
  contacts: [],
  currentContact: null,
  isLoading: false,
  error: null,

  loadContacts: async () => {
    set({ isLoading: true, error: null })
    try {
      const pbContacts = await getAllContacts()
      set({ contacts: pbContacts.map(pbToContact), isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load contacts', isLoading: false })
    }
  },

  loadContact: async (id) => {
    set({ isLoading: true, error: null })
    try {
      const pbContact = await pbGetContact(id)
      set({ currentContact: pbToContact(pbContact), isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to load contact', isLoading: false })
    }
  },

  addContact: async (data) => {
    const pbContact = await pbCreateContact({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      company: data.company,
      position: data.position,
      status: data.status,
      stage: data.stage,
      source: data.source || 'manual',
      coursesInterested: data.coursesInterested,
      lastContact: data.lastContact,
      notes: data.notes,
      assignedTo: data.assignedTo,
    })
    const contact = pbToContact(pbContact)
    set((s) => ({ contacts: [contact, ...s.contacts] }))
    return contact
  },

  updateContact: async (id, data) => {
    const pbData: Record<string, unknown> = {}
    if (data.firstName !== undefined) {pbData.firstName = data.firstName}
    if (data.lastName !== undefined) {pbData.lastName = data.lastName}
    if (data.email !== undefined) {pbData.email = data.email}
    if (data.phone !== undefined) {pbData.phone = data.phone}
    if (data.company !== undefined) {pbData.company = data.company}
    if (data.position !== undefined) {pbData.position = data.position}
    if (data.status !== undefined) {pbData.status = data.status}
    if (data.stage !== undefined) {pbData.stage = data.stage}
    if (data.source !== undefined) {pbData.source = data.source}
    if (data.coursesInterested !== undefined) {pbData.coursesInterested = data.coursesInterested}
    if (data.lastContact !== undefined) {pbData.lastContact = data.lastContact}
    if (data.notes !== undefined) {pbData.notes = data.notes}
    if (data.assignedTo !== undefined) {pbData.assignedTo = data.assignedTo}

    await pbUpdateContact(id, pbData)
    set((s) => ({
      contacts: s.contacts.map((c) => (c.id === id ? { ...c, ...data } : c)),
      currentContact: s.currentContact?.id === id
        ? { ...s.currentContact, ...data }
        : s.currentContact,
    }))
  },

  deleteContact: async (id) => {
    await pbDeleteContact(id)
    set((s) => ({
      contacts: s.contacts.filter((c) => c.id !== id),
      currentContact: s.currentContact?.id === id ? null : s.currentContact,
    }))
  },

  addNote: async (contactId, data) => {
    const note = await pbAddContactNote(contactId, data)
    set((s) => {
      const newNote = pbToNote(note)
      const updatedContacts = s.contacts.map((c) =>
        c.id === contactId
          ? { ...c, notesHistory: [newNote, ...c.notesHistory] }
          : c
      )
      const updatedCurrent = s.currentContact?.id === contactId
        ? { ...s.currentContact, notesHistory: [newNote, ...s.currentContact.notesHistory] }
        : s.currentContact
      return { contacts: updatedContacts, currentContact: updatedCurrent }
    })
  },

  draftEmail: async (contactId, data = {}) => {
    const result = await pbDraftContactEmail(contactId, data)
    return { subject: result.subject, body: result.body }
  },

  sendEmail: async (contactId, data) => {
    await pbSendContactEmail(contactId, data)
  },

  updateStage: async (id, stage) => {
    const updated = await pbUpdateContactStage(id, stage)
    set((s) => ({
      contacts: s.contacts.map((c) => (c.id === id ? pbToContact(updated) : c)),
      currentContact: s.currentContact?.id === id ? pbToContact(updated) : s.currentContact,
    }))
  },
}))

// Helpers
export const STATUS_CONFIG: Record<ContactStatus, { label: string; className: string }> = {
  lead:     { label: 'Lead',      className: 'bg-blue-100 text-blue-700 border border-blue-200' },
  prospect: { label: 'Prospect', className: 'bg-yellow-100 text-yellow-700 border border-yellow-200' },
  client:   { label: 'Client',   className: 'bg-green-100 text-green-700 border border-green-200' },
  inactive: { label: 'Inactive',  className: 'bg-gray-100 text-gray-500 border border-gray-200' },
}

export const STAGE_CONFIG: Record<ContactStage, { label: string; step: number; color: string }> = {
  new:       { label: 'New',      step: 1, color: 'var(--color-slate-400)' },
  contacted: { label: 'Contacted', step: 2, color: 'var(--color-blue-500)' },
  proposal:  { label: 'Proposal',  step: 3, color: 'var(--color-warning)' },
  won:       { label: 'Won',     step: 4, color: 'var(--color-success)' },
  lost:      { label: 'Lost',    step: 0, color: 'var(--color-danger)' },
}

export const SOURCE_CONFIG: Record<ContactSource, { label: string }> = {
  website:         { label: 'Website' },
  landing_booking: { label: 'Landing — Booking' },
  landing_contact: { label: 'Landing — Contact' },
  referral:        { label: 'Referral' },
  social:          { label: 'Social Media' },
  manual:          { label: 'Manual' },
  other:           { label: 'Other' },
}
